/**
 * Portal Payments Page
 * Payment management for business users
 */

import React, { useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { PermissionGate } from '../../components/PermissionGate';
import portalService from '../../services/portal.service';
import type { PortalPayment, PortalPaymentStats } from '../../types/portal.types';

const fmtECV = (n?: number) =>
  Math.round(n ?? 0).toLocaleString('pt-PT', { maximumFractionDigits: 0 });

const PortalPayments: React.FC = () => {
  const { hasPermission } = useAuth();
  const [payments, setPayments] = useState<PortalPayment[]>([]);
  const [stats, setStats] = useState<PortalPaymentStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [periodo, setPeriodo] = useState(30);
  const [estadoFilter, setEstadoFilter] = useState<string>('');
  const [showRefundModal, setShowRefundModal] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState<PortalPayment | null>(null);
  const [refundMotivo, setRefundMotivo] = useState('');

  const loadData = async () => {
    try {
      setLoading(true);
      const paymentsRes = await portalService.getPayments({ periodo, estado: estadoFilter || undefined });
      setPayments(paymentsRes.data || []);
      setStats((paymentsRes as any).stats || null);
    } catch (err: any) {
      setError(err.message || 'Erro ao carregar pagamentos');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [periodo, estadoFilter]);

  const handleRefund = async () => {
    if (!selectedPayment || !refundMotivo.trim()) return;
    try {
      await portalService.requestRefund(selectedPayment.ID, refundMotivo);
      setShowRefundModal(false);
      setSelectedPayment(null);
      setRefundMotivo('');
      loadData();
    } catch (err: any) {
      setError(err.message || 'Erro ao solicitar reembolso');
    }
  };

  const getEstadoLabel = (estado: string) => {
    switch (estado) {
      case 'S': return { text: 'Pago', class: 'bg-green-100 text-green-800' };
      case 'N': return { text: 'Pendente', class: 'bg-yellow-100 text-yellow-800' };
      case 'F': return { text: 'Falhou', class: 'bg-red-100 text-red-800' };
      case 'E': return { text: 'Estornado', class: 'bg-purple-100 text-purple-800' };
      case 'P': return { text: 'Processando', class: 'bg-blue-100 text-blue-800' };
      default: return { text: estado, class: 'bg-gray-100 text-gray-800' };
    }
  };

  if (loading && payments.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pagali-primary"></div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Vendas</h1>
          <p className="text-gray-600">Acompanhe as vendas do seu negocio</p>
        </div>
        <div className="flex gap-4">
          <select
            value={periodo}
            onChange={(e) => setPeriodo(Number(e.target.value))}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pagali-primary"
          >
            <option value={7}>Últimos 7 dias</option>
            <option value={30}>Últimos 30 dias</option>
            <option value={90}>Últimos 90 dias</option>
            <option value={365}>Último ano</option>
            <option value={3650}>Todo o período</option>
          </select>
          <select
            value={estadoFilter}
            onChange={(e) => setEstadoFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pagali-primary"
          >
            <option value="">Todos os estados</option>
            <option value="S">Pagos</option>
            <option value="N">Pendentes</option>
            <option value="F">Falhados</option>
            <option value="E">Estornados</option>
          </select>
        </div>
      </div>

      {/* Error Alert */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-600">{error}</p>
          <button onClick={() => setError(null)} className="text-sm text-red-500 underline mt-1">
            Fechar
          </button>
        </div>
      )}

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {/* Valor Bruto */}
          <div className="bg-white rounded-lg shadow p-5">
            <p className="text-xs text-gray-500 uppercase tracking-wide">Valor Bruto</p>
            <p className="text-xl font-bold text-gray-800 mt-1">
              {fmtECV((stats as any).totalBruto ?? stats.totalRecebido)} ECV
            </p>
            <p className="text-xs text-gray-400 mt-0.5">{(stats as any).pagos ?? 0} transacções pagas</p>
          </div>

          {/* Comissão Pagali */}
          <div className="bg-white rounded-lg shadow p-5">
            <p className="text-xs text-gray-500 uppercase tracking-wide">Comissão Pagali</p>
            <p className="text-xl font-bold text-orange-600 mt-1">
              {fmtECV((stats as any).totalComissao)} ECV
            </p>
            <p className="text-xs text-gray-400 mt-0.5">{(stats as any).comissaoPercentual ?? 0}% + IVA 15%</p>
          </div>

          {/* IVA */}
          <div className="bg-white rounded-lg shadow p-5">
            <p className="text-xs text-gray-500 uppercase tracking-wide">IVA s/ Comissão</p>
            <p className="text-xl font-bold text-amber-600 mt-1">
              {fmtECV((stats as any).totalIVA)} ECV
            </p>
            <p className="text-xs text-gray-400 mt-0.5">15% sobre comissão</p>
          </div>

          {/* Valor Líquido */}
          <div className="bg-white rounded-lg shadow p-5 border border-green-200 bg-green-50">
            <p className="text-xs text-green-700 uppercase tracking-wide font-semibold">Valor Líquido</p>
            <p className="text-xl font-bold text-green-700 mt-1">
              {fmtECV((stats as any).totalLiquido)} ECV
            </p>
            <p className="text-xs text-green-600 mt-0.5">Bruto − comissão − IVA</p>
          </div>

          {/* Pendente */}
          <div className="bg-white rounded-lg shadow p-5">
            <p className="text-xs text-gray-500 uppercase tracking-wide">Pendente</p>
            <p className="text-xl font-bold text-yellow-600 mt-1">
              {fmtECV(stats.totalPendente)} ECV
            </p>
            <p className="text-xs text-gray-400 mt-0.5">{(stats as any).pendentes ?? 0} por cobrar</p>
          </div>

          {/* Total transacções */}
          <div className="bg-white rounded-lg shadow p-5">
            <p className="text-xs text-gray-500 uppercase tracking-wide">Transacções</p>
            <p className="text-xl font-bold text-gray-800 mt-1">{stats.total ?? 0}</p>
            <p className="text-xs text-gray-400 mt-0.5">
              <span className="text-green-600">{(stats as any).pagos ?? 0} pagas</span>
              {' · '}
              <span className="text-yellow-600">{(stats as any).pendentes ?? 0} pendentes</span>
            </p>
          </div>
        </div>
      )}

      {/* Payments Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Referencia
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Cliente
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Servico
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Montante
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Comissão Pagali
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Valor Liquido
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Estado
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Data
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Acoes
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {payments.length === 0 ? (
                <tr>
                  <td colSpan={9} className="px-6 py-12 text-center text-gray-500">
                    Nenhum pagamento encontrado para o periodo selecionado.
                  </td>
                </tr>
              ) : (
                payments.map((payment) => {
                  const estado = getEstadoLabel(payment.ESTADO);
                  return (
                    <tr key={payment.ID} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{payment.REFERENCIA}</div>
                        {payment.NUMERO_AUTORIZACAO && (
                          <div className="text-xs text-gray-500">
                            Auth: {payment.NUMERO_AUTORIZACAO}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{payment.CLIENTE_NOME || '-'}</div>
                        {payment.CLIENTE_EMAIL && (
                          <div className="text-xs text-gray-500">{payment.CLIENTE_EMAIL}</div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {payment.SERVICO || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {fmtECV(payment.MONTANTE)} ECV
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-orange-600">
                          {fmtECV((payment as any).COMISSAO_PAGALI)} ECV
                        </div>
                        <div className="text-xs text-gray-400">
                          ({(payment as any).COMISSAO_PERCENTUAL}%)
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-green-700">
                          {fmtECV((payment as any).VALOR_LIQUIDO)} ECV
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${estado.class}`}>
                          {estado.text}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(payment.DT_REGISTO).toLocaleDateString('pt-CV', { timeZone: 'Atlantic/Cape_Verde' })}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <button className="text-pagali-primary hover:text-pagali-primary/80 mr-3">
                          Ver
                        </button>
                        <PermissionGate permission="MY_PAYMENTS_REFUND">
                          {payment.ESTADO === 'S' && (
                            <button
                              onClick={() => {
                                setSelectedPayment(payment);
                                setShowRefundModal(true);
                              }}
                              className="text-orange-600 hover:text-orange-900"
                            >
                              Reembolsar
                            </button>
                          )}
                        </PermissionGate>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Refund Modal */}
      {showRefundModal && selectedPayment && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold">Solicitar Reembolso</h2>
            </div>
            <div className="p-6 space-y-4">
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-sm text-gray-500">Referencia</p>
                <p className="font-medium">{selectedPayment.REFERENCIA}</p>
                <p className="text-sm text-gray-500 mt-2">Montante</p>
                <p className="font-medium">{fmtECV(selectedPayment.MONTANTE)} ECV</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Motivo do reembolso *
                </label>
                <textarea
                  value={refundMotivo}
                  onChange={(e) => setRefundMotivo(e.target.value)}
                  rows={3}
                  placeholder="Descreva o motivo do reembolso..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pagali-primary"
                />
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowRefundModal(false);
                    setSelectedPayment(null);
                    setRefundMotivo('');
                  }}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleRefund}
                  disabled={!refundMotivo.trim()}
                  className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50"
                >
                  Solicitar Reembolso
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PortalPayments;
